/**
 * Shift breaks (backlog #37). A shift has zero or more breaks, each with a start offset from the shift start,
 * a duration and paid/unpaid. Storing the offset (not a wall-clock time) is what keeps breaks attached when a
 * shift is dragged; resizes keep absolute times where they still fit (see `rebaseBreaks`).
 *
 * t525u: `offset_minutes` may be null, meaning "Any time": the break has no set start and is taken whenever suits.
 * Such a break counts everywhere by its duration (paid hours, cost, timesheets, compliance) but has no position:
 * it is never drawn on a timeline and never assigned to a particular hour.
 *
 * `break_minutes` on a shift stays as the derived total of UNPAID break minutes, which is what every paid-hours
 * and cost calculation deducts (it always meant "unpaid break" before multiple breaks existed).
 */
export interface ShiftBreak {
  /** Minutes after the shift start; null = "Any time" (no set start). */
  offset_minutes: number | null;
  duration_minutes: number;
  paid: boolean;
}
/** A break with a set start time. */
export type TimedBreak = ShiftBreak & { offset_minutes: number };

/** Input form: an offset, a local "HH:mm" start (converted against the shift start), or neither ("Any time"). */
export interface ShiftBreakInput {
  offset_minutes?: number | null;
  start_time?: string | null;
  duration_minutes: number;
  paid?: boolean;
}

export const MAX_BREAKS = 8;

/** t525u: true for an "Any time" break (no set start). Anything that isn't a number counts, so bad data never crashes. */
export const isAnyTime = (b: { offset_minutes?: number | null }): boolean => typeof b.offset_minutes !== 'number' || !Number.isFinite(b.offset_minutes);
export const isTimed = <B extends { offset_minutes?: number | null }>(b: B): b is B & { offset_minutes: number } => !isAnyTime(b);
/** Breaks with a set time only, e.g. for drawing on a timeline or working out who's on break in a given hour. */
export const timedBreaks = <B extends { offset_minutes?: number | null }>(breaks: readonly B[] | null | undefined): (B & { offset_minutes: number })[] => (breaks ?? []).filter(isTimed);

const hm = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
const fmt = (m: number) => `${String(Math.floor(((m % 1440) + 1440) % 1440 / 60)).padStart(2, '0')}:${String(((m % 60) + 60) % 60).padStart(2, '0')}`;

/** Offset of a local HH:mm from a local HH:mm shift start, treating earlier times as after midnight. */
export function offsetFromLocal(shiftStart: string, breakStart: string): number {
  const d = hm(breakStart) - hm(shiftStart);
  return d < 0 ? d + 1440 : d;
}

/** Local HH:mm a break starts, given the shift's local start; null for an "Any time" break. */
export function breakLocalStart(shiftStart: string, b: Pick<ShiftBreak, 'offset_minutes'>): string | null {
  return isAnyTime(b) ? null : fmt(hm(shiftStart) + (b.offset_minutes as number));
}

/** Order for storage and display: timed breaks by start, then "Any time" ones in their given order. */
export function sortBreaks<B extends { offset_minutes?: number | null }>(breaks: readonly B[]): B[] {
  return breaks.map((b, i) => [b, i] as const)
    .sort(([a, i], [b, j]) => (isAnyTime(a) ? 1 : 0) - (isAnyTime(b) ? 1 : 0) || (isTimed(a) && isTimed(b) ? a.offset_minutes - b.offset_minutes : 0) || i - j)
    .map(([b]) => b);
}

/** Input rows -> stored breaks. A row with neither an offset nor a start time is "Any time" (offset null). */
export function normaliseBreaks(input: ShiftBreakInput[] | null | undefined, shiftStart: string): ShiftBreak[] {
  return sortBreaks((input ?? []).map(b => ({
    offset_minutes: typeof b.offset_minutes === 'number' ? b.offset_minutes : b.start_time ? offsetFromLocal(shiftStart, b.start_time) : null,
    duration_minutes: Math.round(b.duration_minutes),
    paid: Boolean(b.paid),
  })));
}

/** Problems with a set of breaks for a shift of `spanMinutes`; empty means valid. */
export function validateBreaks(breaks: ShiftBreak[], spanMinutes: number): string[] {
  const errs: string[] = [];
  if (breaks.length > MAX_BREAKS) errs.push(`At most ${MAX_BREAKS} breaks per shift.`);
  const sorted = sortBreaks(breaks);
  let prev: TimedBreak | null = null, timedN = 0;
  for (const [i, b] of sorted.entries()) {
    const n = `Break ${i + 1}`;
    const off = b.offset_minutes;
    if (!Number.isInteger(b.duration_minutes) || (typeof off === 'number' && !Number.isInteger(off))) errs.push(`${n}: times must be whole minutes.`);
    if (b.duration_minutes < 1) errs.push(`${n}: duration must be at least 1 minute.`);
    if (typeof off !== 'number' || !Number.isFinite(off)) {
      if (b.duration_minutes >= spanMinutes) errs.push(`${n} must be shorter than the shift.`);
      continue;
    }
    timedN++;
    if (off <= 0 || off + b.duration_minutes >= spanMinutes) errs.push(`${n} must start after the shift starts and end before it ends.`);
    if (prev && prev.offset_minutes + prev.duration_minutes > off) errs.push(`${n} overlaps break ${timedN - 1}.`);
    prev = { ...b, offset_minutes: off };
  }
  const unpaid = unpaidBreakMinutes(breaks);
  if (unpaid >= spanMinutes) errs.push('Unpaid breaks must be shorter than the shift.');
  else if (breaks.reduce((s, b) => s + b.duration_minutes, 0) >= spanMinutes) errs.push('Breaks must add up to less than the shift.');
  return errs;
}

export const unpaidBreakMinutes = (breaks: ShiftBreak[] | null | undefined) => (breaks ?? []).filter(b => !b.paid).reduce((n, b) => n + b.duration_minutes, 0);
export const paidBreakMinutes = (breaks: ShiftBreak[] | null | undefined) => (breaks ?? []).filter(b => b.paid).reduce((n, b) => n + b.duration_minutes, 0);
export const longestBreak = (breaks: ShiftBreak[] | null | undefined) => (breaks ?? []).reduce((n, b) => Math.max(n, b.duration_minutes), 0);

/**
 * One break from the old single `break_minutes` (always unpaid), placed in the middle of the shift and rounded
 * to 15 minutes. Used for shifts stored before breaks existed and by the original migration.
 */
export function legacyBreak(spanMinutes: number, breakMinutes: number): TimedBreak[] {
  if (!breakMinutes || breakMinutes <= 0 || breakMinutes >= spanMinutes) return [];
  let offset = Math.round((spanMinutes - breakMinutes) / 2 / 15) * 15;
  offset = Math.min(Math.max(offset, 1), spanMinutes - breakMinutes - 1);
  return [{ offset_minutes: offset, duration_minutes: breakMinutes, paid: false }];
}

/** t525u: a new unpaid break from just a total (e.g. a CSV import or a caller that only sends `break_minutes`): "Any time". */
export function anyTimeBreak(spanMinutes: number, breakMinutes: number): ShiftBreak[] {
  if (!breakMinutes || breakMinutes <= 0 || breakMinutes >= spanMinutes) return [];
  return [{ offset_minutes: null, duration_minutes: Math.round(breakMinutes), paid: false }];
}

/**
 * Re-anchor breaks after a shift's times change. A pure move (same length) keeps offsets, so breaks travel with
 * the shift. A resize keeps each timed break at the same absolute time when it still fits, else keeps its offset;
 * returns null when neither fits so the caller can ask the user to adjust the breaks. "Any time" breaks stay as they are.
 */
export function rebaseBreaks(breaks: ShiftBreak[], oldStart: string, newStart: string, oldSpan: number, newSpan: number): ShiftBreak[] | null {
  if (!breaks.length) return [];
  if (oldSpan === newSpan) return breaks;
  const delta = Math.round((Date.parse(newStart) - Date.parse(oldStart)) / 60000);
  const absolute = breaks.map(b => (isTimed(b) ? { ...b, offset_minutes: b.offset_minutes - delta } : b));
  if (!validateBreaks(absolute, newSpan).length) return absolute;
  if (!validateBreaks(breaks, newSpan).length) return breaks;
  return null;
}

/** Workspace default break rule (UI review "Breaks & time rules"): shifts of at least `min_shift_minutes` get this break. */
export interface BreakRule {
  min_shift_minutes: number;
  duration_minutes: number;
  paid: boolean;
}
export const MAX_BREAK_RULES = 6;
export const DEFAULT_BREAK_RULES: BreakRule[] = [{ min_shift_minutes: 0, duration_minutes: 30, paid: false }];

/** Default breaks for a new shift of `spanMinutes`: the rule with the largest threshold the shift reaches, as an "Any time" break (t525u). */
export function defaultBreaksFor(rules: BreakRule[] | null | undefined, spanMinutes: number): ShiftBreak[] {
  const best = [...(rules ?? DEFAULT_BREAK_RULES)].filter(r => r.duration_minutes > 0 && spanMinutes >= r.min_shift_minutes)
    .sort((a, b) => b.min_shift_minutes - a.min_shift_minutes)[0];
  if (!best) return [];
  return anyTimeBreak(spanMinutes, best.duration_minutes).map(b => ({ ...b, paid: Boolean(best.paid) }));
}

/**
 * t618u: per-position break rules. A position with its own (non-empty) rules uses them instead of the workspace default
 * rules; a position without any falls back to the workspace default. Presets with their own breaks still win (callers
 * apply a preset's breaks before reaching here), and existing shifts never change.
 */
export type PositionBreakRules = Record<string, BreakRule[]>;
export function breakRulesFor(settings: { break_rules?: BreakRule[] | null; position_break_rules?: PositionBreakRules | null } | null | undefined,
  positionId?: string | null): BreakRule[] {
  const own = positionId ? settings?.position_break_rules?.[positionId] : undefined;
  if (own && own.length) return own;
  return settings?.break_rules ?? DEFAULT_BREAK_RULES;
}
/** t618u: default breaks for a new shift in `positionId` (null = no position) of `spanMinutes`. */
export function newShiftBreaks(settings: Parameters<typeof breakRulesFor>[0], positionId: string | null | undefined, spanMinutes: number): ShiftBreak[] {
  return defaultBreaksFor(breakRulesFor(settings, positionId), spanMinutes);
}
/** t618u: the same as phone break rows (no start = "Any time"). */
export function newShiftBreakDrafts(settings: Parameters<typeof breakRulesFor>[0], positionId: string | null | undefined, spanMinutes: number): BreakDraft[] {
  return newShiftBreaks(settings, positionId, spanMinutes).map(b => ({ duration_minutes: b.duration_minutes, paid: b.paid }));
}

/**
 * t183u (phone shift sheet): a break as picked on the phone. `start_time` is optional: t525u - a break without one is
 * "Any time" (offset null). It used to be placed mid-shift for you.
 */
export interface BreakDraft { duration_minutes: number; start_time?: string | null; paid?: boolean }
export const BREAK_PRESETS = [15, 30, 45, 60] as const;

export function placeBreaks(rows: readonly BreakDraft[], shiftStart: string, spanMinutes: number): ShiftBreak[] {
  return sortBreaks(placeBreaksByRow(rows, shiftStart, spanMinutes));
}
/** As `placeBreaks`, in the rows' own order. */
export function placeBreaksByRow(rows: readonly BreakDraft[], shiftStart: string, _spanMinutes?: number): ShiftBreak[] {
  return rows.map(r => ({ offset_minutes: r.start_time ? offsetFromLocal(shiftStart, r.start_time) : null, duration_minutes: Math.round(r.duration_minutes), paid: Boolean(r.paid) }));
}

/** Minutes as "30m" / "1h" / "1h 15m". */
const short = (m: number) => (m >= 60 ? `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ''}` : `${m}m`);

/** "45m" / "45m · 15m paid" for the total of a shift's breaks (unpaid is what comes off paid hours). */
export function breakTotalLabel(breaks: readonly { duration_minutes: number; paid?: boolean }[] | null | undefined): string {
  const list = breaks ?? [];
  const total = list.reduce((n, b) => n + b.duration_minutes, 0);
  const paid = list.filter(b => b.paid).reduce((n, b) => n + b.duration_minutes, 0);
  if (!total) return '';
  return paid ? `${short(total)} · ${paid}m paid` : short(total);
}

/** t525u: "30 min break, any time" / "15 min paid break, any time" for an "Any time" break. */
export function anyTimeBreakLabel(b: { duration_minutes: number; paid?: boolean }, opts: { paid?: boolean } = {}): string {
  return `${b.duration_minutes} min ${opts.paid !== false && b.paid ? 'paid ' : ''}break, any time`;
}

/**
 * One break for display: a timed break as "11:30 30m" (`at` formats its start from the offset), an "Any time" one
 * as "30 min break, any time". `paid` (default true) adds "paid".
 */
export function breakText(b: { offset_minutes?: number | null; duration_minutes: number; paid?: boolean }, at: (offsetMinutes: number) => string, opts: { paid?: boolean } = {}): string {
  if (isAnyTime(b)) return anyTimeBreakLabel(b, opts);
  return `${at(b.offset_minutes as number)} ${b.duration_minutes}m${opts.paid !== false && b.paid ? ' paid' : ''}`;
}
/** Breaks for display: timed ones in start order, then "Any time" ones. */
export const breaksForDisplay = <B extends { offset_minutes?: number | null }>(breaks: readonly B[] | null | undefined): B[] => sortBreaks(breaks ?? []);
